import {
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Textarea
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconPlus } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { slugify } from '@/utils/slugify'
import { generateUUID } from '@/utils/uuid'
import { AlertConditionCard } from './AlertConditionCard'
import { PipelineFormValues } from './VlmPipelineForm'
import {
  ALERT_CONDITION_TEMPLATES,
  AlertConditionTemplate
} from './alertConditionTemplates'

const MAX_CHARS = 10000

type PipelineTriggersStepProps = {
  form: UseFormReturnType<PipelineFormValues>
}

export const PipelineTriggersStep = ({ form }: PipelineTriggersStepProps) => {
  const intl = useIntl()
  const used = form.values.userPrompt.length

  const addedAlertTags = new Set(
    form.values.alertPrompts.map((alertPrompt) => alertPrompt.alert_tag)
  )
  const availableTemplates = ALERT_CONDITION_TEMPLATES.filter(
    (template) => !addedAlertTags.has(slugify(template.name))
  )

  const handleAddAlertCondition = () => {
    form.insertListItem('alertPrompts', {
      uuid: generateUUID(),
      name: '',
      description: '',
      alert_tag: '',
      severity: 'medium'
    })
  }

  const handleAddAlertConditionTemplate = (
    template: AlertConditionTemplate
  ) => {
    form.insertListItem('alertPrompts', {
      uuid: generateUUID(),
      name: template.name,
      description: template.description,
      alert_tag: slugify(template.name),
      severity: 'medium'
    })
  }

  return (
    <Paper p="lg" bg="gray.1">
      <Stack gap="md">
        <div>
          <Textarea
            label={intl.formatMessage({
              id: 'vlm.pipelines.create.userPrompt.label'
            })}
            description={intl.formatMessage({
              id: 'vlm.pipelines.create.userPrompt.description'
            })}
            {...form.getInputProps('userPrompt')}
            minRows={5}
            maxRows={15}
            maxLength={MAX_CHARS}
            autosize
          />
          <Text
            size="xs"
            c={used > MAX_CHARS - 100 ? 'red' : 'dimmed'}
            ta="right"
            mt={4}
          >
            {used} / {MAX_CHARS}
          </Text>
        </div>

        <Divider />

        {form.values.pipelineType === 'virtual_patrol' && (
          <PlatformAlert variant="info" compact>
            <FormattedMessage id="vlm.pipelines.create.patrol.triggers.info" />
          </PlatformAlert>
        )}

        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              <FormattedMessage id="vlm.pipelines.create.alertConditions.title" />
            </Text>
            <Text size="xs" c="dimmed">
              <FormattedMessage id="vlm.pipelines.create.alertConditions.description" />
            </Text>
          </Stack>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddAlertCondition}
          >
            <FormattedMessage id="vlm.pipelines.create.alertConditions.addButton" />
          </Button>
        </Group>

        <Stack gap="xs">
          <Text size="xs" fw={500} c="dimmed">
            <FormattedMessage id="vlm.pipelines.create.alertConditions.templates.title" />
          </Text>
          <Group gap="xs">
            {availableTemplates.map((template) => (
              <Button
                key={template.name}
                variant="outline"
                size="compact-xs"
                onClick={() => handleAddAlertConditionTemplate(template)}
              >
                {template.name}
              </Button>
            ))}
          </Group>
        </Stack>

        {form.values.alertPrompts.map((alertPrompt, index) => (
          <AlertConditionCard
            key={alertPrompt.uuid}
            form={form}
            index={index}
          />
        ))}

        <Group justify="flex-end">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddAlertCondition}
          >
            <FormattedMessage id="vlm.pipelines.create.alertConditions.addButton" />
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}
